import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import GetAppIcon from '@material-ui/icons/GetApp';
import SearchIcon from '@material-ui/icons/Search';
import { saveAs } from 'file-saver';
import React, { useCallback, useMemo, useState } from 'react';
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useHistory, useLocation } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import { Modal } from '../../../../components';
import { useAuth } from '../../../../context';
import { Props as TableProps } from '../../../../core-ui/table/Table';
import {
  ClaimStoreProgramParticipant,
  ClaimStoreProgramParticipantVariables,
} from '../../../../generated/ClaimStoreProgramParticipant';
import {
  DownloadStoreProgramParticipantAdvance,
  DownloadStoreProgramParticipantAdvanceVariables,
} from '../../../../generated/DownloadStoreProgramParticipantAdvance';
import {
  GetProgramsAdvance,
  GetProgramsAdvanceVariables,
} from '../../../../generated/GetProgramsAdvance';
import {
  StoreProgramParticipantAdvance,
  StoreProgramParticipantAdvanceVariables,
} from '../../../../generated/StoreProgramParticipantAdvance';
import {
  ClaimStoreType,
  CmsParticipantStatus,
  StoreProgramParticipantActiveTab,
  StoreProgramParticipantOrderByType,
  StoreProgramStatus,
} from '../../../../generated/globalTypes';
import { CLAIM_STORE_PROGRAM_PARTICIPANT } from '../../../../graphql/mutations/programParticipant';
import { GET_PROGRAM_ADVANCE } from '../../../../graphql/queries';
import {
  DOWNLOAD_PROGRAM_PARTICIPANT_ADVANCE,
  GET_PROGRAM_PARTICIPANT,
} from '../../../../graphql/queries/participantList';
import { formatProgramNameWithYear } from '../../../../helpers';
import { useTableSort } from '../../../../hooks';
import { Dropdown } from '../../components';
import SearchableCheckboxDropdown from '../../components/dropdowns/SearchableCheckboxDropdown';
import { routePaths } from '../../constants/routes';
import { colors, spacing } from '../../constants/theme';
import { Button, DatePicker, Table, Text, TextInput } from '../../core-ui';

type LocationState = {
  programId: string;
  programName: string;
  filters?: ScreenFilters;
};

type ParticipantRow = StoreProgramParticipantAdvance['storeProgramParticipantAdvance']['row'][0];
type ParticipantStructure = TableProps<
  ParticipantRow,
  Record<string, unknown>
>['structure'];

const tabStatusMap = {
  WAITING: [StoreProgramStatus.WAITING],
  IN_PROGRESS: [StoreProgramStatus.WAITING, StoreProgramStatus.UNVERIFIED],
  VERIFIED: [StoreProgramStatus.VERIFIED],
};

const getStatusDisplay = (status: CmsParticipantStatus) => {
  switch (status) {
    case CmsParticipantStatus.VERIFIED:
      return { text: 'Verified', color: 'green' as const };
    case CmsParticipantStatus.UNVERIFIED:
      return { text: 'Unverified', color: 'red' as const };
    case CmsParticipantStatus.REUPLOAD:
      return { text: 'Reupload', color: 'orange' as const };
    case CmsParticipantStatus.WAITING:
      return { text: 'Waiting', color: 'grey' as const };
    default:
      // Should only in progress left here.
      return { text: 'In Progress', color: 'grey' as const };
  }
};

const formatDate = (date: string | null | undefined) => {
  if (!date) {
    return '-';
  }
  return new Date(date).toLocaleString('id', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  });
};

const PROGRAM_DISPLAY: Record<
  string,
  { label: string; color: 'blue' | 'navy' }
> = {
  bintang: { label: 'Bintang Rucika', color: 'blue' },
  cap: { label: 'Cap Rumah', color: 'navy' },
};

const getProgramDisplay = (slug: string | null | undefined) => {
  if (!slug) {
    return null;
  }
  const key = Object.keys(PROGRAM_DISPLAY).find((k) =>
    slug.toLowerCase().startsWith(k),
  );
  return key ? PROGRAM_DISPLAY[key] : null;
};

export type ScreenFilters = {
  selectedTab: StoreProgramParticipantActiveTab;
  searchInput: string;
  selectedCities: Array<string>;
  startDate: Date | null;
  endDate: Date | null;
  page: number;
  rowsPerPage: number;
  programId: string;
  selectedYear: number;
};

const currentYear = new Date().getFullYear();

const defaultFiltersVal: ScreenFilters = {
  page: 0,
  rowsPerPage: 10,
  searchInput: '',
  selectedCities: [],
  selectedTab: StoreProgramParticipantActiveTab.WAITING,
  endDate: null,
  startDate: null,
  programId: '',
  selectedYear: currentYear,
};

export function NewProgramParticipantPage() {
  const admin = useAuth();
  const location = useLocation<LocationState>();
  const history = useHistory();

  const currentFilters = location.state?.filters;

  const [cityOpen, setCityOpen] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(
    currentFilters ? currentFilters.searchInput : '',
  );
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [showProgramExportError, setShowProgramExportError] = useState(false);

  const MIN_YEAR = 2025;
  const yearOptions = Array.from(
    { length: currentYear - MIN_YEAR + 1 },
    (_, i) => {
      const year = MIN_YEAR + i;
      return { label: String(year), value: year };
    },
  );

  const [filters, setFilters] = useState<ScreenFilters>(
    currentFilters || defaultFiltersVal,
  );
  let {
    rowsPerPage,
    page,
    selectedCities,
    selectedTab,
    endDate,
    searchInput,
    startDate,
    programId,
    selectedYear,
  } = filters;

  const onSetFilters = useCallback(
    (newFilters: Partial<ScreenFilters>) => {
      setFilters({ ...filters, ...newFilters });
    },
    [filters],
  );

  const onYearChange = useCallback(
    (year: number) => {
      onSetFilters({ selectedYear: year, programId: '', page: 0 });
    },
    [onSetFilters],
  );

  const applySelectedCities = useCallback(
    (values: Array<string>) => {
      onSetFilters({ selectedCities: values, page: 0 });
    },
    [onSetFilters],
  );

  const { order, orderBy, orderByVariable, onRequestSort } = useTableSort<
    StoreProgramParticipantOrderByType
  >();

  const [debouncedCallback] = useDebouncedCallback((val: string) => {
    setDebouncedSearch(val);
    onSetFilters({ page: 0 });
  }, 500);

  const dateFilter = useMemo(() => {
    if (filters.startDate && filters.endDate) {
      return {
        startDate: filters.startDate.toISOString(),
        endDate: filters.endDate.toISOString(),
      };
    }
    return null;
  }, [filters.startDate, filters.endDate]);

  const queryVariables = useMemo(() => {
    let currentDateFilter;
    if (dateFilter && dateFilter.endDate && dateFilter.startDate) {
      switch (selectedTab) {
        case StoreProgramParticipantActiveTab.WAITING:
          currentDateFilter = { firstSubmitDataDate: dateFilter };
          break;
        case StoreProgramParticipantActiveTab.IN_PROGRESS:
          currentDateFilter = { lastUpdatedDate: dateFilter };
          break;
        case StoreProgramParticipantActiveTab.VERIFIED:
          currentDateFilter = { verifiedDate: dateFilter };
          break;
      }
    }
    return {
      pagination: { first: rowsPerPage, skip: page * rowsPerPage },
      searchName: debouncedSearch || undefined,
      city: selectedCities.length ? selectedCities : undefined,
      status: tabStatusMap[selectedTab],
      orderBy: orderByVariable ?? undefined,
      activeTab: selectedTab,
      programId: programId || undefined,
      programYear: selectedYear.toString(),
      ...currentDateFilter,
    };
  }, [
    selectedTab,
    rowsPerPage,
    page,
    debouncedSearch,
    selectedCities,
    orderByVariable,
    programId,
    selectedYear,
    dateFilter,
  ]);

  const { data: programList } = useQuery<
    GetProgramsAdvance,
    GetProgramsAdvanceVariables
  >(GET_PROGRAM_ADVANCE, {
    variables: {
      where: {
        year: selectedYear.toString(),
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data, loading, error, refetch } = useQuery<
    StoreProgramParticipantAdvance,
    StoreProgramParticipantAdvanceVariables
  >(GET_PROGRAM_PARTICIPANT, {
    variables: queryVariables,
    fetchPolicy: 'network-only',
  });

  const [downloadData] = useLazyQuery<
    DownloadStoreProgramParticipantAdvance,
    DownloadStoreProgramParticipantAdvanceVariables
  >(DOWNLOAD_PROGRAM_PARTICIPANT_ADVANCE, {
    onCompleted: async ({ downloadStoreProgramParticipantAdvance }) => {
      let res = await fetch(downloadStoreProgramParticipantAdvance.link);
      let blob = await res.blob();
      saveAs(
        blob,
        `Status ${
          tabs.find(({ key }) => key === selectedTab)?.label
        } - Pemenang Program Loyalty - ${formatProgramNameWithYear(
          selectedProgram?.name,
          queryVariables.programYear,
        )}.csv`,
      );
      setConfirmationModalOpen(false);
    },
  });

  const [claimParticipant] = useMutation<
    ClaimStoreProgramParticipant,
    ClaimStoreProgramParticipantVariables
  >(CLAIM_STORE_PROGRAM_PARTICIPANT, {
    onCompleted: () => refetch(),
  });

  let selectedProgram = useMemo(() => {
    return programList?.getProgramsAdvance.find(({ id }) => id === programId);
  }, [programId, programList]);

  const rows = data?.storeProgramParticipantAdvance.row ?? [];
  const total = data?.storeProgramParticipantAdvance.total ?? 0;

  const onChangeRowsPerPage = useCallback(
    (newRows: number) => {
      onSetFilters({ rowsPerPage: newRows, page: 0 });
    },
    [onSetFilters],
  );

  const getbuttonProps = (rowData: ParticipantRow) => {
    const isClaimed = Boolean(rowData.admin);
    const isDiffAdmin = rowData.admin?.id !== admin.id;
    const status = rowData.status;
    const reUpload = Boolean(rowData.isLastReuploadAt);
    if (selectedTab === 'WAITING') {
      if (!isClaimed) {
        return {
          title: 'Klaim',
          onPress: () => {
            claimParticipant({
              variables: {
                programId: rowData.StoreProgram?.id || '',
                storeId: rowData.store.id,
                type: ClaimStoreType.CLAIM,
              },
            });
          },
          disabled: false,
        };
      } else {
        return {
          title: 'Diklaim',
          backgroundColor: colors.button.background.disabled,
          onPress: () => {},
          disabled: true,
        };
      }
    } else if (selectedTab === 'IN_PROGRESS') {
      if (rowData.admin && isDiffAdmin) {
        return {
          title: 'Sudah diklaim',
          backgroundColor: 'transparent',
          onPress: () => {},
          disabled: true,
        };
      }
      if (reUpload && !rowData.admin) {
        return {
          title: 'Klaim',
          onPress: () => {
            claimParticipant({
              variables: {
                programId: rowData.StoreProgram?.id || '',
                storeId: rowData.store.id,
                type: ClaimStoreType.CLAIM,
              },
            });
          },
          disabled: false,
        };
      }
      if (status === StoreProgramStatus.UNVERIFIED && !reUpload) {
        return {
          title: 'Lihat Detail',
          backgroundColor: colors.button.background.darkGrey,
          onPress: () => {
            history.push({
              pathname: routePaths.wipParticipant,
              state: {
                data: rowData,
                programName: selectedProgram?.name,
                viewOnly: true,
                filters,
                programId: programId,
              },
            });
          },
          disabled: false,
        };
      }
      return {
        title: 'Verifikasi',
        iconRight: (
          <ArrowForwardIcon
            fontSize="small"
            htmlColor={colors.text.white}
            style={{ marginLeft: 8 }}
          />
        ),
        onPress: () => {
          history.push({
            pathname: routePaths.wipParticipant,
            state: {
              data: rowData,
              questionare: rowData.checklistQuestions,
              programName: selectedProgram?.name,
              filters,
              programId: programId,
            },
          });
        },
        disabled: false,
      };
    } else {
      return {
        title: 'Lihat Detail',
        onPress: () => {
          history.push({
            pathname: routePaths.wipParticipant,
            state: {
              data: rowData,
              programName: selectedProgram?.name,
              viewOnly: true,
              filters,
              programId: programId,
            },
          });
        },
      };
    }
  };

  const tabs = [
    {
      index: 0,
      key: StoreProgramParticipantActiveTab.WAITING,
      label: 'Waiting',
      count: data?.storeProgramParticipantAdvance.totalWaiting || 0,
    },
    {
      index: 1,
      key: StoreProgramParticipantActiveTab.IN_PROGRESS,
      label: 'In Progress',
      count: data?.storeProgramParticipantAdvance.totalInProgress || 0,
    },
    {
      index: 2,
      key: StoreProgramParticipantActiveTab.VERIFIED,
      label: 'Verified',
      count: data?.storeProgramParticipantAdvance.totalVerified || 0,
    },
  ];

  const sharedColumns: ParticipantStructure = {
    storeName: {
      headerTitle: 'Nama Toko',
      orderable: true,
      render: ({ store }) => <Text bold>{store?.storeName ?? '-'}</Text>,
    },
    managerStoreCode: {
      headerTitle: 'Kode Toko',
      orderable: true,
      render: ({ store }) => <Text>{store?.managerStoreCode ?? '-'}</Text>,
    },
    storeCity: {
      headerTitle: 'Kota/Kabupaten',
      orderable: true,
      render: ({ store }) => <Text>{store?.cityName ?? '-'}</Text>,
    },
  };
  const lastSharedColumn: ParticipantStructure = {
    status: {
      headerTitle: 'Status',
      orderable: true,
      render: ({ cmsParticipantStatus }) => {
        const s = getStatusDisplay(cmsParticipantStatus);
        return (
          <Text bold color={s.color}>
            {s.text}
          </Text>
        );
      },
    },
    action: {
      noHeaderTitle: true,
      render: (rowData) => {
        let {
          title,
          backgroundColor,
          iconRight,
          disabled,
          onPress,
        } = getbuttonProps(rowData);
        return (
          <Button
            title={title}
            style={{
              backgroundColor:
                backgroundColor || colors.background.secondaryBlue,
            }}
            titleTextProps={
              title === 'Sudah diklaim'
                ? {
                    color: 'grey',
                  }
                : undefined
            }
            icon={iconRight ? iconRight : undefined}
            iconAndText
            rightIcon
            disabled={disabled}
            onPress={onPress}
          />
        );
      },
    },
  };

  const structure = useMemo((): ParticipantStructure => {
    // WAITING — minimal columns + Klaim button
    if (selectedTab === 'WAITING') {
      return {
        ...sharedColumns,
        firstSubmitDataAt: {
          headerTitle: 'Tanggal Upload Dokumen',
          orderable: true,
          render: ({ firstSubmitDataAt }) => (
            <Text>{formatDate(firstSubmitDataAt)}</Text>
          ),
        },
        programName: {
          headerTitle: 'Program Loyalty',
          orderable: true,
          render: ({ StoreProgram }) => {
            const display = getProgramDisplay(StoreProgram?.name);
            return (
              <Text bold color={display?.color ?? 'blue'}>
                {display?.label ?? StoreProgram?.name ?? '-'}
              </Text>
            );
          },
        },
        ...lastSharedColumn,
      };
    }

    // IN PROGRESS — full columns + Verifikasi/Lihat Detail
    if (selectedTab === 'IN_PROGRESS') {
      return {
        ...sharedColumns,
        firstSubmitDataAt: {
          headerTitle: 'Tanggal Upload Dokumen',
          orderable: true,
          render: ({ firstSubmitDataAt }) => (
            <Text>{formatDate(firstSubmitDataAt)}</Text>
          ),
        },
        confirmAt: {
          headerTitle: 'Tanggal Proses Verifikasi Terakhir',
          orderable: true,
          render: ({ updatedAt }) => <Text>{formatDate(updatedAt)}</Text>,
        },
        programName: {
          headerTitle: 'Program Loyalty',
          orderable: true,
          render: ({ StoreProgram }) => {
            const display = getProgramDisplay(StoreProgram?.name);
            return (
              <Text bold color={display?.color ?? 'blue'}>
                {display?.label ?? StoreProgram?.name ?? '-'}
              </Text>
            );
          },
        },
        claimedBy: {
          headerTitle: 'Diklaim Oleh',
          orderable: true,
          render: ({ admin }) => <Text>{admin?.name ?? '-'}</Text>,
        },
        confirmBy: {
          headerTitle: 'Verifikasi Terakhir oleh',
          orderable: true,
          render: ({ confirmBy }) => <Text>{confirmBy ?? '-'}</Text>,
        },
        ...lastSharedColumn,
      };
    }

    // VERIFIED — date columns change + Lihat Detail only
    return {
      ...sharedColumns,
      createdAt: {
        headerTitle: 'Tanggal Verifikasi Dibuat',
        orderable: true,
        render: ({ firstSubmitDataAt }) => (
          <Text>{formatDate(firstSubmitDataAt)}</Text>
        ),
      },
      updatedAt: {
        headerTitle: 'Tanggal Verifikasi Diperbarui',
        orderable: true,
        render: ({ updatedAt }) => <Text>{formatDate(updatedAt)}</Text>,
      },
      claimedBy: {
        headerTitle: 'Diklaim Oleh',
        orderable: true,
        render: ({ admin }) => <Text>{admin?.name ?? '-'}</Text>,
      },
      confirmBy: {
        headerTitle: 'Diperbarui Terakhir',
        orderable: true,
        render: ({ confirmBy }) => <Text>{confirmBy ?? '-'}</Text>,
      },
      ...lastSharedColumn,
    };
  }, [selectedTab, sharedColumns, lastSharedColumn]);

  const handleExport = (all?: boolean) => {
    let { pagination, ...rest } = queryVariables;
    if (all && selectedYear) {
      downloadData({
        variables: { programYear: selectedYear.toString() },
      });
    } else {
      downloadData({ variables: { ...rest } });
    }
  };

  return (
    <ScrollView contentContainerStyle={styles.root}>
      <View style={styles.topSection}>
        <View style={styles.topLeftColumn}>
          <View style={styles.breadcrumb}>
            <Text color="blue" size="s">
              Pemenang Program Principal
            </Text>
          </View>
          <View style={styles.yearDropdown}>
            <Text size="xs" style={styles.yearDropdownLabel}>
              Pilih Tahun
            </Text>
            <Dropdown
              containerStyle={styles.yearDropdownSelect}
              value={selectedYear}
              options={yearOptions}
              onChange={onYearChange}
            />
          </View>
        </View>

        <View style={styles.topRightColumn}>
          <TouchableOpacity
            style={styles.totalRow}
            onPress={() => {
              if (!programId) {
                setShowProgramExportError(true);
                return;
              }
              handleExport(true);
            }}
          >
            <Text size="m" bold>
              Jumlah Data{' '}
              <Text
                size="m"
                style={{ color: colors.background.secondaryBlue }}
                bold
              >
                ({total} Toko)
              </Text>
            </Text>
            <GetAppIcon
              fontSize="small"
              htmlColor={colors.background.secondaryBlue}
              style={{ marginLeft: spacing.xxs }}
            />
          </TouchableOpacity>

          <TextInput
            value={searchInput}
            onChangeText={(val) => {
              onSetFilters({ searchInput: val, page: 0 });
              debouncedCallback(val);
            }}
            placeholder="Cari Nama/Kode Toko"
            leftNode={<SearchIcon htmlColor={colors.text.grey} />}
            containerStyle={styles.searchInput}
          />
        </View>
      </View>

      <View style={styles.tabsRow}>
        {tabs.map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[styles.tab, selectedTab === tab.key && styles.tabSelected]}
            onPress={() => {
              let { selectedYear, ...otherFilter } = defaultFiltersVal;
              setDebouncedSearch('');
              onSetFilters({
                ...otherFilter,
                selectedTab: tab.key,
                page: 0,
              });
            }}
          >
            <Text
              bold={selectedTab === tab.key}
              color={selectedTab === tab.key ? 'black' : 'grey'}
            >
              {tab.label}
            </Text>
            <View style={styles.countBadge}>
              <Text size="xs" color="white">
                {tab.count}
              </Text>
            </View>
          </TouchableOpacity>
        ))}
      </View>

      <View style={styles.filtersRow}>
        <View style={styles.filterLeft}>
          <View style={styles.filterItem}>
            <Text size="xs" style={styles.filterLabel}>
              Pilih Kota/Kabupaten
            </Text>
            <SearchableCheckboxDropdown
              selectedValues={selectedCities}
              onApply={applySelectedCities}
              open={cityOpen}
              onOpenChange={setCityOpen}
              containerStyle={{ width: 200 }}
              searchPlaceholder="Cari Kabupaten/Kota"
            />
          </View>

          <View style={styles.filterItem}>
            <Text size="xs" style={styles.filterLabel}>
              Pilih Program
            </Text>
            <Dropdown
              value={programId || null}
              options={
                programList?.getProgramsAdvance.map(({ id, name }) => ({
                  value: id,
                  label: name,
                })) || []
              }
              onChange={(newVal: string) => {
                onSetFilters({ programId: newVal, page: 0 });
                setShowProgramExportError(false);
              }}
              placeholder="Semua"
            />
            {showProgramExportError && !programId && (
              <Text size="xs" color="red" style={styles.dateError}>
                {'Pilih satu program yang akan diexport'}
              </Text>
            )}
          </View>

          <View style={styles.filterItem}>
            <Text size="xs" style={styles.filterLabel}>
              Pilih Tanggal
            </Text>
            <View style={styles.dateRow}>
              <DatePicker
                selectedDate={startDate}
                onChange={(date) => onSetFilters({ startDate: date, page: 0 })}
                placeholder="Tanggal Awal"
                style={styles.datePicker}
                icon={<ExpandMoreIcon fontSize="small" />}
                helperText={!startDate && endDate ? 'Masukan Tanggal Awal' : ''}
                error={Boolean(!startDate && endDate)}
              />
              <Text size="s" style={styles.dateSeparator}>
                -
              </Text>
              <DatePicker
                selectedDate={endDate}
                onChange={(date) => onSetFilters({ endDate: date, page: 0 })}
                placeholder="Tanggal Akhir"
                style={styles.datePicker}
                icon={<ExpandMoreIcon fontSize="small" />}
                minDate={startDate ?? undefined}
                helperText={
                  startDate && !endDate ? 'Masukan Tanggal Akhir' : ''
                }
                error={Boolean(startDate && !endDate)}
              />
            </View>
            {startDate && endDate && endDate < startDate && (
              <Text size="xs" color="red" style={styles.dateError}>
                {'Tanggal akhir tidak boleh sebelum tanggal awal'}
              </Text>
            )}
          </View>
        </View>

        <Button
          title="Export Data"
          preset="primary"
          onPress={() => {
            if (!programId) {
              setShowProgramExportError(true);
              return;
            }
            const mandatoryKeys = new Set([
              'programYear',
              'activeTab',
              'pagination',
              'status',
              'programId',
            ]);
            const hasExtraFilter = Object.entries(queryVariables).some(
              ([key, value]) => {
                if (mandatoryKeys.has(key)) {
                  return false;
                }
                if (Array.isArray(value)) {
                  return value.length > 0;
                }
                return value != null && value !== undefined && value !== '';
              },
            );
            if (!hasExtraFilter) {
              setConfirmationModalOpen(true);
            } else {
              handleExport();
            }
          }}
          style={styles.exportButton}
          icon={<GetAppIcon htmlColor={colors.button.color.primary} />}
          iconAndText
          titleTextProps={{ style: { marginLeft: spacing.xxs } }}
        />
      </View>
      <Modal
        open={isConfirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
      >
        <View style={styles.confirmationModalContent}>
          <Text bold size="xl" style={styles.confirmationModalTitle}>
            {'Anda yakin akan export data tahun ini?'}
          </Text>
          <Text color="grey" style={styles.confirmationModalBody}>
            {
              'Anda akan melakukan export semua data [waiting/in progress/verified] tahun ini. Anda juga bisa pilih export data pada tanggal yang diinginkan'
            }
          </Text>
          <View style={styles.confirmationModalButton}>
            <View
              style={[
                styles.confirmationModalButtonItem,
                { marginRight: spacing.m },
              ]}
            >
              <Button
                title={'Batalkan'}
                fullWidth
                large
                onPress={() => setConfirmationModalOpen(false)}
                style={styles.confirmationModalBatalkanButton}
              />
            </View>
            <View style={styles.confirmationModalButtonItem}>
              <Button
                title={'Export Data'}
                preset="primary"
                fullWidth
                large
                onPress={() => handleExport()}
                isLoading={loading}
              />
            </View>
          </View>
        </View>
      </Modal>

      <Table
        tableStyle={{ borderCollapse: 'collapse', borderSpacing: 0 }}
        data-testid="new-program-participant-table"
        striped
        loading={loading}
        data={rows}
        page={page}
        rowsPerPage={rowsPerPage}
        dataCount={
          selectedTab === 'WAITING'
            ? data?.storeProgramParticipantAdvance.totalWaiting
            : selectedTab === 'IN_PROGRESS'
            ? data?.storeProgramParticipantAdvance.totalInProgress
            : data?.storeProgramParticipantAdvance.totalVerified
        }
        onChangePage={(page) => onSetFilters({ page })}
        onChangeRowsPerPage={onChangeRowsPerPage}
        order={order}
        orderBy={orderBy}
        onRequestSort={onRequestSort}
        error={!!error}
        headerStyle={{ backgroundColor: '#ebebf0', opacity: 0.7 }}
        structure={structure}
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  confirmationModalContent: {
    paddingHorizontal: spacing.l,
    paddingTop: spacing.l,
    paddingBottom: spacing.m,
    alignItems: 'center',
  },
  confirmationModalTitle: {
    textAlign: 'center',
    marginBottom: spacing.m,
  },
  confirmationModalBody: {
    textAlign: 'center',
    marginBottom: spacing.l,
  },
  confirmationModalButton: {
    flexDirection: 'row',
    width: '100%',
  },
  confirmationModalButtonItem: {
    flex: 1,
  },
  confirmationModalBatalkanButton: {
    backgroundColor: '#e53c3c',
  },
  root: {
    flexGrow: 1,
    backgroundColor: colors.background.grey,
    padding: spacing.l,
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.s,
  },
  yearDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  yearDropdownLabel: {
    marginRight: spacing.s,
  },
  yearDropdownSelect: {
    width: 100,
  },
  topSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: spacing.l,
  },
  topLeftColumn: {},
  topRightColumn: {
    alignItems: 'flex-end',
  },
  totalRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  searchInput: {
    width: 220,
    marginTop: spacing.xxs,
  },
  tabsRow: {
    flexDirection: 'row',
    marginBottom: spacing.l,
    justifyContent: 'center',
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.s,
    paddingHorizontal: spacing.m,
    borderWidth: 1,
    borderColor: colors.border.primary,
    marginVertical: spacing.xxs,
    marginHorizontal: 1,
  },
  tabSelected: {
    backgroundColor: colors.background.activeBlue,
    borderColor: colors.button.background.primary,
  },
  countBadge: {
    backgroundColor: colors.background.secondaryBlue,
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: spacing.xxs,
  },
  filtersRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.m,
    backgroundColor: colors.background.white,
    paddingVertical: spacing.m,
    borderRadius: 4,
  },
  filterLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  filterItem: {
    marginRight: spacing.l,
  },
  filterLabel: {
    marginBottom: spacing.xxs,
  },
  dateRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  datePicker: {
    width: 140,
  },
  dateSeparator: {
    marginHorizontal: spacing.xxs,
    marginTop: 10,
  },
  dateError: {
    marginTop: spacing.xxs,
    color: 'red',
    minHeight: 16,
  },
  exportButton: {
    backgroundColor: colors.background.secondaryBlue,
    minWidth: 140,
  },
});
